// src/services/authService.js
import { auth, googleProvider } from '../firebase';
import { 
  signInWithPopup, 
  signInWithRedirect,
  getRedirectResult,
  signOut, 
  onAuthStateChanged
} from 'firebase/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://globalexpedyte-backend-217354974895.us-central1.run.app/api';

class AuthService {
  constructor() {
    this.currentUser = null;
    this.authStateListeners = [];
    this.retryCount = 0;
    this.maxRetries = 3;
    
    // Listen to Firebase auth state changes
    onAuthStateChanged(auth, (user) => {
      this.currentUser = user;
      this.notifyAuthStateListeners(user);
    });

    // Check for redirect result on app load
    this.handleRedirectResult();
  }

  // Handle redirect result for mobile/COOP issues
  async handleRedirectResult() {
    try {
      const result = await getRedirectResult(auth);
      if (result && result.user) {
        console.log('Handling redirect result...');
        await this.processGoogleAuthResult(result);
      }
    } catch (error) {
      console.error('Error handling redirect result:', error);
    }
  }

  // Process Google authentication result (shared between popup and redirect)
  async processGoogleAuthResult(result) {
    const idToken = await result.user.getIdToken();
    
    const response = await this.apiRequest('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ idToken }),
    });

    if (response.token) {
      this.setToken(response.token);
      this.setCurrentUser(response.user);
    }

    // Dispatch global auth change event
    window.dispatchEvent(new CustomEvent('authChange', { 
      detail: { type: 'login', user: response.user } 
    }));

    return response;
  }

  // Auth state management
  addAuthStateListener(callback) {
    this.authStateListeners.push(callback);
    callback(this.currentUser);
    
    return () => {
      this.authStateListeners = this.authStateListeners.filter(cb => cb !== callback);
    };
  }

  notifyAuthStateListeners(user) {
    this.authStateListeners.forEach(callback => callback(user));
  }

  // Token management
  getToken() {
    return localStorage.getItem('token');
  }

  setToken(token) {
    if (!token) {
      console.warn('Attempting to set empty token');
      return;
    }
    localStorage.setItem('token', token);
  }

  removeToken() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  // User management
  getCurrentUser() {
    try {
      const user = localStorage.getItem('user');
      return user ? JSON.parse(user) : null;
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
      localStorage.removeItem('user');
      return null;
    }
  }

  setCurrentUser(user) {
    if (!user) {
      console.warn('Attempting to set empty user');
      return;
    }
    localStorage.setItem('user', JSON.stringify(user));
  }

  isAuthenticated() {
    const token = this.getToken();
    if (!token) return false;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) return false;
      
      const payload = JSON.parse(atob(parts[1]));
      const expirationTime = payload.exp * 1000;
      const currentTime = Date.now();
      const bufferTime = 5 * 60 * 1000;
      
      return expirationTime > (currentTime + bufferTime);
    } catch (error) {
      console.error('Error validating token:', error);
      this.removeToken();
      return false;
    }
  }

  // Enhanced API request helper with retry logic
  async apiRequest(endpoint, options = {}, retryCount = 0) {
    const token = this.getToken();
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
      
      // Handle 503 Service Unavailable with retry logic
      if (response.status === 503 && retryCount < this.maxRetries) {
        const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff
        console.log(`Service unavailable, retrying in ${delay}ms... (attempt ${retryCount + 1})`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.apiRequest(endpoint, options, retryCount + 1);
      }
      
      if (response.status === 401) {
        this.removeToken();
        throw new Error('Session expired. Please log in again.');
      }
      
      if (response.status === 403) {
        throw new Error('Access denied. You don\'t have permission for this action.');
      }
      
      if (response.status === 404) {
        throw new Error('Resource not found.');
      }
      
      if (response.status === 503) {
        throw new Error('Our servers are temporarily unavailable. Please try again in a few minutes.');
      }
      
      if (response.status >= 500) {
        throw new Error('Server error. Please try again later.');
      }
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ 
          message: `Request failed with status ${response.status}` 
        }));
        throw new Error(error.message || 'API request failed');
      }

      return response.json();
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      throw error;
    }
  }

  // Enhanced Google Sign-In with fallback to redirect
  async signInWithGoogle() {
    try {
      console.log('Attempting Google sign-in...');
      
      // First, try popup method
      try {
        const result = await signInWithPopup(auth, googleProvider);
        
        if (!result.user) {
          throw new Error('No user data received from Google');
        }
        
        return await this.processGoogleAuthResult(result);
        
      } catch (popupError) {
        console.log('Popup failed, trying redirect method...', popupError);
        
        // If popup fails due to COOP or other issues, fallback to redirect
        if (popupError.code === 'auth/popup-blocked' || 
            popupError.code === 'auth/popup-closed-by-user' ||
            popupError.message.includes('Cross-Origin-Opener-Policy')) {
          
          // Use redirect method instead
          await signInWithRedirect(auth, googleProvider);
          // The result will be handled by handleRedirectResult on page load
          return { redirected: true };
        }
        
        throw popupError;
      }
      
    } catch (error) {
      console.error('Google sign-in error:', error);
      
      // Handle specific Google Auth errors
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign-in cancelled. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('Popup blocked. Please allow popups for this site and try again.');
      } else if (error.code === 'auth/network-request-failed') {
        throw new Error('Network error. Please check your internet connection.');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Too many sign-in attempts. Please wait a few minutes and try again.');
      } else if (error.message.includes('Service Unavailable') || error.message.includes('503')) {
        throw new Error('Our authentication service is temporarily unavailable. Please try again in a few minutes.');
      } else if (error.message.includes('Session expired')) {
        throw error;
      } else if (error.redirected) {
        throw new Error('Redirecting to complete sign-in...');
      } else {
        throw new Error('Google sign-in failed. Please try again or contact support if the issue persists.');
      }
    }
  }

  // Registration with validation
  async register(userData) {
    if (!userData.email || !userData.password) {
      throw new Error('Email and password are required');
    }
    
    if (userData.password.length < 6) {
      throw new Error('Password must be at least 6 characters long');
    }

    const response = await this.apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    
    return response;
  }

  // Login with validation
  async login(credentials) {
    if (!credentials.email || !credentials.password) {
      throw new Error('Email and password are required');
    }

    const response = await this.apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });

    if (response.token) {
      this.setToken(response.token);
      this.setCurrentUser(response.user);
    }

    return response;
  }

  // Enhanced logout
  async logout() {
    try {
      if (auth.currentUser) {
        await signOut(auth);
      }
    } catch (error) {
      console.warn('Firebase signout error:', error);
    }
    
    try {
      if (this.getToken()) {
        await this.apiRequest('/auth/logout', { method: 'POST' }).catch(() => {});
      }
    } catch (error) {
      console.warn('Backend logout error:', error);
    }
    
    this.removeToken();
    window.location.href = '/';
  }

  // Password reset with enhanced error handling
  async resetPassword(email) {
    if (!email) {
      throw new Error('Email is required');
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new Error('Please enter a valid email address');
    }

    try {
      return await this.apiRequest('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
    } catch (error) {
      // Provide more specific error messages for password reset
      if (error.message.includes('Service Unavailable') || error.message.includes('503')) {
        throw new Error('Password reset service is temporarily unavailable. Please try again in a few minutes.');
      }
      throw error;
    }
  }

  // Health check method
  async checkServiceHealth() {
    try {
      const response = await fetch(`${API_BASE_URL}/health`, {
        method: 'GET',
        timeout: 5000
      });
      return response.ok;
    } catch (error) {
      console.error('Service health check failed:', error);
      return false;
    }
  }

  // All other methods remain the same...
  async verifyEmail(token) {
    if (!token) {
      throw new Error('Verification token is required');
    }
    
    const response = await this.apiRequest(`/auth/verify-email?token=${encodeURIComponent(token)}`);
    return response;
  }

  async getUserDashboard() {
    if (!this.isAuthenticated()) {
      throw new Error('Please log in to view your dashboard');
    }
    
    return this.apiRequest('/user/dashboard');
  }

  async processPayment(cartItems, totalAmount) {
    if (!this.isAuthenticated()) {
      throw new Error('Please log in to process payment');
    }
    
    if (!cartItems || cartItems.length === 0) {
      throw new Error('Cart is empty');
    }
    
    if (!totalAmount || totalAmount <= 0) {
      throw new Error('Invalid payment amount');
    }

    return this.apiRequest('/payfast/process', {
      method: 'POST',
      body: JSON.stringify({ cartItems, totalAmount }),
    });
  }

  async changePassword(currentPassword, newPassword) {
    if (!this.isAuthenticated()) {
      throw new Error('Please log in to change your password');
    }
    
    if (!currentPassword || !newPassword) {
      throw new Error('Current password and new password are required');
    }
    
    if (newPassword.length < 6) {
      throw new Error('New password must be at least 6 characters long');
    }
    
    if (currentPassword === newPassword) {
      throw new Error('New password must be different from current password');
    }

    return this.apiRequest('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async updateProfile(profileData) {
    if (!this.isAuthenticated()) {
      throw new Error('Please log in to update your profile');
    }
    
    if (!profileData || Object.keys(profileData).length === 0) {
      throw new Error('Profile data is required');
    }

    const response = await this.apiRequest('/auth/update-profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
    
    if (response.user) {
      this.setCurrentUser(response.user);
    }
    
    return response;
  }

  async refreshToken() {
    try {
      if (auth.currentUser) {
        const idToken = await auth.currentUser.getIdToken(true);
        
        const response = await this.apiRequest('/auth/refresh', {
          method: 'POST',
          body: JSON.stringify({ idToken }),
        });
        
        if (response.token) {
          this.setToken(response.token);
          this.setCurrentUser(response.user);
        }
        
        return response;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error;
    }
  }

  // Cart Management Methods
  async getCart() {
    if (!this.isAuthenticated()) {
      throw new Error('Please log in to view your cart');
    }
    
    return this.apiRequest('/cart');
  }

  async addToCart(product, quantity = 1) {
    if (!this.isAuthenticated()) {
      throw new Error('Please log in to add items to cart');
    }

    if (!product.id || !product.name || !product.price) {
      throw new Error('Invalid product data');
    }

    return this.apiRequest('/cart/add', {
      method: 'POST',
      body: JSON.stringify({
        productId: product.id,
        productName: product.name,
        price: product.price,
        quantity,
        image: product.image,
        category: product.category
      }),
    });
  }

  async updateCartItem(productId, quantity) {
    if (!this.isAuthenticated()) {
      throw new Error('Please log in to update your cart');
    }

    if (quantity < 0) {
      throw new Error('Quantity cannot be negative');
    }

    return this.apiRequest(`/cart/update/${productId}`, {
      method: 'PUT',
      body: JSON.stringify({ quantity }),
    });
  }

  async removeFromCart(productId) {
    if (!this.isAuthenticated()) {
      throw new Error('Please log in to modify your cart');
    }

    return this.apiRequest(`/cart/remove/${productId}`, {
      method: 'DELETE',
    });
  }

  async clearCart() {
    if (!this.isAuthenticated()) {
      throw new Error('Please log in to clear your cart');
    }

    return this.apiRequest('/cart/clear', {
      method: 'DELETE',
    });
  }

  async getCartAnalytics(days = 30) {
    if (!this.isAuthenticated()) {
      throw new Error('Please log in to view analytics');
    }

    return this.apiRequest(`/analytics/cart-insights?days=${days}`);
  }

  async getAllActiveCarts() {
    if (!this.isAuthenticated()) {
      throw new Error('Please log in to view cart data');
    }

    return this.apiRequest('/admin/carts');
  }
}

const authService = new AuthService();
export default authService;